/**
 * L'équipe du tenant — chunk 9.AE.1.
 *
 * Ferme `D-NEW-WEB-NO-USERS-SCREEN` (backend complet, aucun écran) et
 * `D-NEW-WEB-BROKERS-READ-ONLY` (mutations backend existantes, jamais exposées).
 *
 * ## Un écran, deux natures, jamais confondues
 *
 * Un courtier **est** un utilisateur — `broker_profiles.user_id` est à la fois clé
 * primaire et étrangère. Les lister séparément aurait donné deux écrans qui
 * mentent l'un sur l'autre : désactiver quelqu'un ici et le voir encore actif là.
 *
 * La colonne « rôle » distingue ce qui doit l'être : administrateur du cabinet,
 * courtier, contact client. Et le libellé dit le métier, pas l'enum.
 *
 * ## L'interface reflète les règles, elle ne les applique pas
 *
 * Les gardes vivent côté serveur (9.Y.1) : refus des clés d'API, interdiction de
 * l'escalade de rôle, interdiction de changer son propre rôle ou de se désactiver.
 * On grise ici ce que le serveur refusera, pour épargner un aller-retour — mais un
 * bouton masqué n'a jamais protégé personne, et c'est le serveur qui décide.
 */

import { Fragment, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { useAuth } from "../../contexts/AuthContext";
import {
  useBrokerProfiles,
  useDeactivateMember,
  useResendInvite,
  useTeamMembers,
  type TeamMember,
  type TeamRole,
} from "../../lib/queries/team";
import { toast } from "../../lib/toast";

import { EditBrokerProfileDialog } from "./_parts/EditBrokerProfileDialog";
import { InviteMemberDialog } from "./_parts/InviteMemberDialog";
import { MemberInvitationHistory } from "./_parts/MemberInvitationHistory";
import { MemberRoleDialog } from "./_parts/MemberRoleDialog";

/** Les rôles, dans l'ordre où un administrateur les lit : du plus large au plus étroit. */
const ORDRE_ROLES: readonly TeamRole[] = [
  "super_admin",
  "tenant_admin",
  "broker",
  "client_contact",
];

const TON_STATUT: Record<string, string> = {
  active: "bg-emerald-50 text-emerald-800 ring-emerald-200",
  pending: "bg-amber-50 text-amber-800 ring-amber-200",
  disabled: "bg-slate-100 text-slate-600 ring-slate-200",
};

export function TeamPage() {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const [inviteOuvert, setInviteOuvert] = useState(false);
  const [roleDe, setRoleDe] = useState<TeamMember | null>(null);
  const [profilDe, setProfilDe] = useState<TeamMember | null>(null);
  const [aDesactiver, setADesactiver] = useState<TeamMember | null>(null);

  // 9.AV.1 — quel membre est en cours de renvoi. Un booléen global désactiverait
  // le bouton de TOUTES les lignes ; ici seule la ligne concernée s'immobilise.
  const [renvoiEnCours, setRenvoiEnCours] = useState<string | null>(null);

  const membres = useTeamMembers();
  const courtiers = useBrokerProfiles();
  const desactiver = useDeactivateMember();
  const renvoyer = useResendInvite();

  /**
   * Renvoie l'invitation d'un membre en attente.
   *
   * Le double envoi est bloqué **par la ligne** : tant que `renvoiEnCours` porte
   * son identifiant, le bouton est désactivé. Le serveur a de toute façon le
   * dernier mot — sa clé de dédoublonnage empêche deux courriels simultanés — mais
   * un bouton qui reste cliquable pendant l'attente invite à cliquer deux fois.
   */
  async function renvoyerInvitation(m: TeamMember) {
    if (renvoiEnCours !== null) return;
    setRenvoiEnCours(m.id);
    try {
      const r = await renvoyer.mutateAsync(m.id);
      // Distinguer les deux cas : « je viens d'en renvoyer une » n'est pas
      // « il y en avait déjà une qui attendait ».
      toast.success(
        r.already_queued
          ? t("team.resend.already_queued", { email: m.email })
          : t("team.resend.sent", { email: m.email }),
      );
    } catch {
      toast.error(t("team.resend.failed"));
    } finally {
      setRenvoiEnCours(null);
    }
  }

  const estAdmin = user?.role === "tenant_admin" || user?.role === "super_admin";

  // Qui possède un profil de courtier. Sert uniquement à proposer l'édition du
  // profil là où elle a un sens — pas à décider du rôle, qui vient de `users`.
  const avecProfil = useMemo(
    () => new Set((courtiers.data?.items ?? []).map((b) => b.id)),
    [courtiers.data],
  );

  const liste = useMemo(() => {
    const items = membres.data?.items ?? [];
    return [...items].sort((a, b) => {
      const ra = ORDRE_ROLES.indexOf(a.role);
      const rb = ORDRE_ROLES.indexOf(b.role);
      if (ra !== rb) return ra - rb;
      return (a.full_name ?? a.email).localeCompare(b.full_name ?? b.email, "fr");
    });
  }, [membres.data]);

  if (membres.isPending) {
    return (
      <section className="p-6">
        <p className="text-sm text-slate-500" aria-live="polite">
          {t("team.loading")}
        </p>
      </section>
    );
  }

  if (membres.isError) {
    return (
      <section className="p-6">
        <p className="text-sm text-red-800" role="alert">
          {t("team.error")}
        </p>
      </section>
    );
  }

  return (
    <section className="p-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-slate-900">{t("team.title")}</h1>
          <p className="mt-1 text-sm text-slate-600">{t("team.subtitle")}</p>
        </div>
        {estAdmin && (
          <button
            type="button"
            onClick={() => setInviteOuvert(true)}
            className="bg-brand-600 hover:bg-brand-700 focus-visible:ring-brand-500 rounded-lg px-3 py-2 text-sm font-medium text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
          >
            {t("team.invite")}
          </button>
        )}
      </div>

      {liste.length === 0 ? (
        <p className="mt-6 rounded-xl border border-dashed border-slate-300 bg-white px-4 py-8 text-center text-sm text-slate-600">
          {t("team.empty")}
        </p>
      ) : (
        <div className="mt-6 rounded-xl border border-slate-200 bg-white shadow-sm">
          <table className="block w-full text-sm lg:table">
            <thead className="hidden border-b border-slate-200 bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500 lg:table-header-group">
              <tr>
                <th
                  scope="col"
                  className="block min-w-0 px-3 py-2 font-medium lg:table-cell lg:px-4 lg:py-3"
                >
                  {t("team.col.name")}
                </th>
                <th
                  scope="col"
                  className="block min-w-0 px-3 py-2 font-medium lg:table-cell lg:px-4 lg:py-3"
                >
                  {t("team.col.role")}
                </th>
                <th
                  scope="col"
                  className="block min-w-0 px-3 py-2 font-medium lg:table-cell lg:px-4 lg:py-3"
                >
                  {t("team.col.status")}
                </th>
                <th
                  scope="col"
                  className="block min-w-0 px-3 py-2 font-medium lg:table-cell lg:px-4 lg:py-3"
                >
                  {t("team.col.last_login")}
                </th>
                <th
                  scope="col"
                  className="block min-w-0 px-3 py-2 text-right font-medium lg:table-cell lg:px-4 lg:py-3"
                >
                  <span className="sr-only">{t("team.col.actions")}</span>
                </th>
              </tr>
            </thead>
            <tbody className="block lg:table-row-group">
              {liste.map((m) => {
                // Le serveur refuse de changer son propre rôle et de se désactiver
                // (`USERS_SELF_ROLE_CHANGE_FORBIDDEN`, `USERS_SELF_DELETE_FORBIDDEN`).
                // On grise plutôt que de laisser cliquer vers un 400.
                const cestMoi = user?.id === m.id;
                return (
                  <Fragment key={m.id}>
                    <tr className="block p-3 pb-0 lg:table-row lg:p-0">
                      <td className="block min-w-0 px-3 py-2 lg:table-cell lg:px-4 lg:py-3">
                        <span className="block font-medium text-slate-900">
                          {m.full_name ?? "—"}
                          {cestMoi && (
                            <span className="ml-2 text-xs font-normal text-slate-500">
                              {t("team.you")}
                            </span>
                          )}
                        </span>
                        <span className="block break-all text-xs text-slate-500">{m.email}</span>
                      </td>
                      <td className="block min-w-0 px-3 py-2 text-slate-700 lg:table-cell lg:px-4 lg:py-3">
                        {t(`team.role.${m.role}`)}
                      </td>
                      <td className="block min-w-0 px-3 py-2 lg:table-cell lg:px-4 lg:py-3">
                        <span
                          className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset ${
                            TON_STATUT[m.status] ?? TON_STATUT.disabled
                          }`}
                        >
                          {t(`team.status.${m.status}`)}
                        </span>
                      </td>
                      <td className="block min-w-0 px-3 py-2 text-slate-600 lg:table-cell lg:px-4 lg:py-3">
                        <span className="block text-xs text-slate-500 lg:hidden">
                          {t("team.col.last_login")}
                        </span>
                        {m.last_login_at !== null
                          ? new Date(m.last_login_at).toLocaleString(i18n.language)
                          : t("team.never_connected")}
                      </td>
                      <td className="block min-w-0 px-3 py-2 text-right lg:table-cell lg:px-4 lg:py-3">
                        {estAdmin && (
                          <span className="inline-flex flex-wrap justify-start gap-2 lg:justify-end">
                            <button
                              type="button"
                              disabled={cestMoi}
                              title={cestMoi ? t("team.self_role_blocked") : undefined}
                              onClick={() => setRoleDe(m)}
                              className="focus-visible:ring-brand-500 min-h-11 rounded-lg border border-slate-300 px-2.5 py-1 text-xs font-medium text-slate-700 hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 disabled:opacity-40"
                            >
                              {t("team.action.role")}
                            </button>
                            {m.status === "pending" && (
                              <button
                                type="button"
                                disabled={renvoiEnCours !== null}
                                aria-busy={renvoiEnCours === m.id}
                                onClick={() => void renvoyerInvitation(m)}
                                className="border-brand-200 text-brand-700 hover:bg-brand-50 focus-visible:ring-brand-500 min-h-11 rounded-lg border px-2.5 py-1 text-xs font-medium focus-visible:outline-none focus-visible:ring-2 disabled:opacity-40"
                              >
                                {renvoiEnCours === m.id
                                  ? t("team.resend.pending")
                                  : t("team.action.resend_invite")}
                              </button>
                            )}
                            {avecProfil.has(m.id) && (
                              <button
                                type="button"
                                onClick={() => setProfilDe(m)}
                                className="focus-visible:ring-brand-500 min-h-11 rounded-lg border border-slate-300 px-2.5 py-1 text-xs font-medium text-slate-700 hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2"
                              >
                                {t("team.action.broker_profile")}
                              </button>
                            )}
                            {m.status !== "disabled" && (
                              <button
                                type="button"
                                disabled={cestMoi}
                                title={cestMoi ? t("team.self_disable_blocked") : undefined}
                                onClick={() => setADesactiver(m)}
                                className="min-h-11 rounded-lg border border-red-200 px-2.5 py-1 text-xs font-medium text-red-700 hover:bg-red-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 disabled:opacity-40"
                              >
                                {t("team.action.deactivate")}
                              </button>
                            )}
                          </span>
                        )}
                      </td>
                    </tr>
                    <tr className="block border-b border-slate-200 px-3 pb-3 last:border-b-0 lg:table-row lg:p-0">
                      <td colSpan={5} className="block min-w-0 px-3 pb-3 lg:table-cell lg:px-4">
                        <MemberInvitationHistory member={m} />
                      </td>
                    </tr>
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {inviteOuvert && <InviteMemberDialog onClose={() => setInviteOuvert(false)} />}
      {roleDe !== null && <MemberRoleDialog membre={roleDe} onClose={() => setRoleDe(null)} />}
      {profilDe !== null && (
        <EditBrokerProfileDialog membre={profilDe} onClose={() => setProfilDe(null)} />
      )}

      {aDesactiver !== null && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="team-deact-title"
        >
          <div className="w-full max-w-md rounded-xl border border-slate-200 bg-white p-5 shadow-lg">
            <h2 id="team-deact-title" className="text-base font-semibold text-slate-900">
              {t("team.deactivate.title")}
            </h2>
            {/* Le serveur fait un soft-delete : `deleted_at` + `status='disabled'`.
                Le dire évite de promettre une destruction qui n'a pas lieu — et
                rassure l'administrateur qui hésite. */}
            <p className="mt-2 text-sm text-slate-600">
              {t("team.deactivate.body", { name: aDesactiver.full_name ?? aDesactiver.email })}
            </p>
            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setADesactiver(null)}
                className="focus-visible:ring-brand-500 min-h-11 rounded-lg border border-slate-300 px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2"
              >
                {t("common.cancel")}
              </button>
              <button
                type="button"
                disabled={desactiver.isPending}
                onClick={() => {
                  desactiver.mutate(aDesactiver.id, { onSettled: () => setADesactiver(null) });
                }}
                className="rounded-lg bg-red-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-red-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2 disabled:opacity-50"
              >
                {desactiver.isPending ? t("team.deactivate.working") : t("team.deactivate.confirm")}
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

export default TeamPage;
